import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import LiveTrackingMap from "./LiveTrackingMap";
import { OrderI } from "../utils/types";
import socketClient from "../utils/socketClient";
import { SOCKET_EVENTS } from "../utils/socketEvents";
import { orderService } from "../services";
import { parseGeoPoint } from "../utils/helpers";
import defaultImage from "../assets/images/cuisines/default.jpeg";

const STATUS_LABELS: Record<string, string> = {
  pending: "Order Placed",
  placed: "Order Placed",
  confirmed: "Order Confirmed",
  preparing: "Being Prepared",
  ready_for_pickup: "Ready for Pickup",
  out_for_delivery: "Out for Delivery",
  on_the_way: "On the Way",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const PendingOrders = ({
  orders,
  onOrderUpdated,
}: {
  orders: OrderI[];
  onOrderUpdated?: (order: OrderI) => void;
  onBack?: () => void;
}) => {
  const [order, setOrder] = useState<OrderI | null>(orders[0] || null);
  const [driverLocation, setDriverLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [cookingInstructions, setCookingInstructions] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [sendingCooking, setSendingCooking] = useState(false);
  const [sendingDelivery, setSendingDelivery] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setOrder(orders[0] || null);
    setCookingInstructions(orders[0]?.cookingInstructions || "");
    setDeliveryInstructions(orders[0]?.deliveryInstructions || "");
  }, [orders]);

  useEffect(() => {
    if (!order?._id) return;

    const poll = async () => {
      try {
        const [detailRes, trackRes]: any[] = await Promise.all([
          orderService.getOrderById(order._id),
          orderService.trackOrder(order._id).catch(() => null),
        ]);

        if (detailRes?.status && detailRes?.data) {
          setOrder(detailRes.data);
          onOrderUpdated?.(detailRes.data);
        }

        const trackData = trackRes?.data || trackRes;
        if (trackData) {
          const trackedRestaurant =
            parseGeoPoint(trackData.restaurantLocation) ||
            parseGeoPoint(trackData.pickupLocation) ||
            parseGeoPoint(trackData.restaurant?.geoLocation);
          const trackedDelivery =
            parseGeoPoint(trackData.deliveryLocation) ||
            parseGeoPoint(trackData.dropLocation) ||
            parseGeoPoint(trackData.deliveryAddress?.geoLocation);
          if (trackedRestaurant || trackedDelivery) {
            setOrder((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                restaurant: trackedRestaurant
                  ? {
                      ...prev.restaurant,
                      geoLocation: {
                        type: "Point",
                        coordinates: [
                          trackedRestaurant.lng,
                          trackedRestaurant.lat,
                        ],
                      },
                    }
                  : prev.restaurant,
                deliveryAddress: trackedDelivery
                  ? {
                      ...prev.deliveryAddress,
                      geoLocation: {
                        type: "Point",
                        coordinates: [
                          trackedDelivery.lng,
                          trackedDelivery.lat,
                        ],
                      },
                    }
                  : prev.deliveryAddress,
              };
            });
          }
        }
        const loc =
          trackData?.deliveryAgentLocation ||
          trackData?.driverLocation ||
          trackData?.location ||
          trackData?.agentLocation;
        if (loc && typeof loc.lat === "number" && typeof loc.lng === "number") {
          setDriverLocation({ lat: loc.lat, lng: loc.lng });
        } else if (
          Array.isArray(loc?.coordinates) &&
          loc.coordinates.length >= 2
        ) {
          setDriverLocation({
            lat: loc.coordinates[1],
            lng: loc.coordinates[0],
          });
        }

        if (trackData?.orderStatus && detailRes?.data) {
          setOrder((prev) =>
            prev ? { ...prev, orderStatus: trackData.orderStatus } : prev
          );
        }
      } catch {
        // ignore polling errors
      }
    };

    poll();
    const interval = window.setInterval(poll, 15000);
    return () => window.clearInterval(interval);
  }, [order?._id, onOrderUpdated]);

  useEffect(() => {
    if (!order?._id) return;

    const handleOrderUpdate = (data: {
      orderId: string;
      orderStatus?: string;
    }) => {
      if (data.orderId !== order._id) return;

      if (data.orderStatus) {
        setOrder((prev) =>
          prev ? { ...prev, orderStatus: data.orderStatus! } : prev
        );
      } else {
        orderService.getOrderById(order._id).then((res: any) => {
          if (res?.status && res?.data) {
            setOrder(res.data);
            onOrderUpdated?.(res.data);
          }
        });
      }
    };

    socketClient.connect();
    socketClient.emit("order:join", { orderId: order._id });
    socketClient.on(SOCKET_EVENTS.ORDER_STATUS_UPDATE, handleOrderUpdate);

    return () => {
      socketClient.emit("order:leave", { orderId: order._id });
      socketClient.off(SOCKET_EVENTS.ORDER_STATUS_UPDATE, handleOrderUpdate);
    };
  }, [order?._id, onOrderUpdated]);

  useEffect(() => {
    if (!order?._id) return;
    const status = (order.orderStatus || "").toLowerCase();
    const isOutForDelivery =
      status === "out_for_delivery" || status === "on_the_way";

    if (!isOutForDelivery) return;

    const handleDriverLocation = (data: {
      lat: number;
      lng: number;
      orderId?: string;
      location?: { lat: number; lng: number; coordinates?: number[] };
    }) => {
      if (data.orderId && data.orderId !== order._id) return;
      if (typeof data.lat === "number" && typeof data.lng === "number") {
        setDriverLocation({ lat: data.lat, lng: data.lng });
        return;
      }
      const loc = data.location;
      if (loc && typeof loc.lat === "number" && typeof loc.lng === "number") {
        setDriverLocation({ lat: loc.lat, lng: loc.lng });
      }
    };

    socketClient.on(SOCKET_EVENTS.DELIVERY_AGENT_LOCATION, handleDriverLocation);
    return () => {
      socketClient.off(
        SOCKET_EVENTS.DELIVERY_AGENT_LOCATION,
        handleDriverLocation
      );
    };
  }, [order?._id, order?.orderStatus]);

  const getRestaurantLocation = () =>
    parseGeoPoint(order?.restaurant?.geoLocation) ||
    parseGeoPoint(order?.restaurant?.location) ||
    parseGeoPoint((order as any)?.restaurantLocation) ||
    parseGeoPoint((order as any)?.pickupLocation);

  const getDeliveryLocation = () =>
    parseGeoPoint(order?.deliveryAddress?.geoLocation) ||
    parseGeoPoint(order?.deliveryAddress) ||
    parseGeoPoint((order as any)?.deliveryLocation) ||
    parseGeoPoint((order as any)?.dropLocation);

  const sendInstruction = async (
    type: "cooking" | "delivery",
    text: string
  ) => {
    if (!order?._id) return;
    const value = text.trim();
    if (!value) {
      toast.error(
        type === "cooking"
          ? "Please enter cooking instructions"
          : "Please enter delivery instructions"
      );
      return;
    }

    const setSending =
      type === "cooking" ? setSendingCooking : setSendingDelivery;
    setSending(true);
    try {
      const payload =
        type === "cooking"
          ? { cookingInstructions: value }
          : { deliveryInstructions: value };
      await orderService.updateOrderInstructions(order._id, payload);
      setOrder((prev) =>
        prev
          ? {
              ...prev,
              ...payload,
            }
          : prev
      );
      toast.success(
        type === "cooking"
          ? "Cooking instructions sent"
          : "Delivery instructions sent"
      );
    } catch (error: any) {
      toast.error(
        error?.message ||
          (type === "cooking"
            ? "Failed to send cooking instructions"
            : "Failed to send delivery instructions")
      );
    } finally {
      setSending(false);
    }
  };

  const handleSendCooking = () =>
    sendInstruction("cooking", cookingInstructions);
  const handleSendDelivery = () =>
    sendInstruction("delivery", deliveryInstructions);

  if (!order) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 dark:text-gray-400">No pending orders</p>
      </div>
    );
  }

  const restaurantLocation = getRestaurantLocation();
  const deliveryLocation = getDeliveryLocation();
  const statusLabel =
    STATUS_LABELS[order.orderStatus?.toLowerCase()] || order.orderStatus;

  const kitchenAddress = [
    order.restaurant?.location?.address,
    order.restaurant?.location?.city,
    order.restaurant?.location?.state,
    order.restaurant?.location?.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  const kitchenImage =
    order.restaurant?.kitchenPhotos?.[0] ||
    order.restaurant?.foodItemImages?.[0] ||
    defaultImage;

  return (
    <div className="bg-[#F7F1EA] dark:bg-gray-900 min-h-full pb-8">
      <div className="px-3 md:px-6 pt-2 md:pt-4">
        {restaurantLocation || deliveryLocation ? (
          <div className="rounded-2xl overflow-hidden">
            <LiveTrackingMap
              restaurantLocation={restaurantLocation}
              deliveryLocation={deliveryLocation}
              driverLocation={driverLocation}
              restaurantLabel={
                order.restaurant?.businessName || "Restaurant"
              }
              deliveryLabel="Delivery location"
            />
          </div>
        ) : (
          <div className="h-[220px] rounded-2xl bg-black flex flex-col items-center justify-center text-white">
            <p className="font-urbanist font-semibold mb-1">Google Maps</p>
            <p className="text-sm text-white/70 px-6 text-center">
              Status: {statusLabel}. Location coordinates not available yet.
            </p>
          </div>
        )}
      </div>

      <div className="px-3 md:px-6 -mt-4 relative z-10">
        <div className="flex items-center gap-2 mb-3 bg-white dark:bg-gray-800 rounded-xl px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
          <input
            type="text"
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            placeholder="Add Delivery Instructions"
            className="flex-1 min-w-0 text-[#6A6A6A] dark:text-gray-300 dark:placeholder-gray-500 font-urbanist text-sm outline-none bg-transparent"
          />
          <button
            type="button"
            onClick={handleSendDelivery}
            disabled={sendingDelivery}
            className="flex-shrink-0 px-4 py-2 rounded-lg bg-[#7402A7] text-white font-urbanist text-sm font-semibold disabled:opacity-60"
          >
            {sendingDelivery ? "..." : "Send"}
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-4">
          <button
            type="button"
            onClick={() => setSummaryExpanded((v) => !v)}
            className="w-full flex justify-between items-center"
          >
            <p className="text-[#050505] dark:text-white font-urbanist text-base font-semibold">
              Order Summary
            </p>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              className={`transition-transform ${summaryExpanded ? "rotate-90" : ""}`}
            >
              <path
                d="M9 18l6-6-6-6"
                stroke="#050505"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {summaryExpanded && (
            <>
              {/* Kitchen details */}
              <div className="flex gap-3 items-start mt-4 pb-4 border-b border-[#EFEFEF] dark:border-gray-700">
                <img
                  src={kitchenImage}
                  alt={order.restaurant?.businessName || "Kitchen"}
                  className="h-14 w-14 rounded-xl object-cover flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.src = defaultImage;
                  }}
                />
                <div className="min-w-0">
                  <p className="text-[#050505] dark:text-white font-urbanist text-[15px] font-semibold">
                    {order.restaurant?.businessName || "Restaurant"}
                  </p>
                  <p className="text-[#8A8A8A] dark:text-gray-400 font-urbanist text-xs mt-1 leading-relaxed">
                    {kitchenAddress || "Address not available"}
                  </p>
                </div>
              </div>

              {/* Items */}
              <div className="py-3 border-b border-[#EFEFEF] dark:border-gray-700 space-y-2">
                {order.items?.map((item) => (
                  <div
                    key={item._id}
                    className="flex justify-between items-center gap-3"
                  >
                    <p className="text-[#050505] dark:text-white font-urbanist text-sm">
                      {item.quantity} x {item.dishName}
                    </p>
                    <p className="text-[#050505] dark:text-white font-urbanist text-sm whitespace-nowrap">
                      ₹{Number(item.totalItemPrice).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Price breakdown */}
              <div className="py-3 border-b border-[#EFEFEF] dark:border-gray-700 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#8A8A8A] dark:text-gray-400">Subtotal</span>
                  <span className="text-[#050505] dark:text-white">
                    ₹{Number(order.subtotal || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#8A8A8A] dark:text-gray-400">Tax</span>
                  <span className="text-[#050505] dark:text-white">
                    ₹{Number(order.taxAmount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#8A8A8A] dark:text-gray-400">
                    Delivery Fee
                  </span>
                  <span className="text-[#050505] dark:text-white">
                    ₹{Number(order.deliveryFee || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center py-3 border-b border-[#EFEFEF] dark:border-gray-700">
                <p className="text-[#050505] dark:text-white font-urbanist text-base font-bold">
                  Total
                </p>
                <p className="text-[#050505] dark:text-white font-urbanist text-base font-bold">
                  ₹{Number(order.totalAmount).toFixed(2)}
                </p>
              </div>

              {/* Cooking instructions */}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/support/chat?${new URLSearchParams({
                      orderId: order._id,
                      topic: "Order help",
                      initialMessage: `Hi, I need help with my order ${order._id}.`,
                    }).toString()}`
                  )
                }
                className="w-full mt-3 py-2.5 rounded-xl border border-[#7402A7] text-[#7402A7] dark:text-purple-400 font-urbanist text-sm font-semibold"
              >
                Need Help?
              </button>

              <div className="flex items-center gap-2 mt-3 bg-[#F5F5F5] dark:bg-gray-700 rounded-xl px-3 py-2">
                <input
                  type="text"
                  value={cookingInstructions}
                  onChange={(e) => setCookingInstructions(e.target.value)}
                  placeholder="Add cooking Instructions"
                  className="flex-1 min-w-0 text-[#6A6A6A] dark:text-gray-300 dark:placeholder-gray-500 font-urbanist text-sm outline-none bg-transparent"
                />
                <button
                  type="button"
                  onClick={handleSendCooking}
                  disabled={sendingCooking}
                  className="flex-shrink-0 px-4 py-2 rounded-lg bg-[#7402A7] text-white font-urbanist text-sm font-semibold disabled:opacity-60"
                >
                  {sendingCooking ? "..." : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingOrders;
